/**
 * wallet/budget.ts — pure budget mathematics for the Roamer Wallet™.
 *
 * All functions are deterministic and side-effect free: they operate on
 * home-currency amounts the module has already converted (transactions with
 * no known exchange rate are EXCLUDED and surfaced via unconvertibleCount —
 * never guessed). Every metric that lacks data is null, not zero.
 */

import type {
  BudgetHealth,
  BudgetSnapshot,
  CityCost,
  SpendingCategory,
  WalletProfile,
} from './models';

const DAY = 24 * 60 * 60 * 1000;

/** A transaction already resolved into home currency (or excluded). */
export interface HomeAmount {
  kind: 'income' | 'expense';
  amount: number; // home currency, positive
  category: SpendingCategory | null;
  city: string | null;
  at: number;
}

export function monthStart(now: number): number {
  const d = new Date(now);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}

export function daysInMonth(now: number): number {
  const d = new Date(now);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
}

export function yearStart(now: number): number {
  return Date.UTC(new Date(now).getUTCFullYear(), 0, 1);
}

function inRange(at: number, from: number, to: number): boolean {
  return at >= from && at < to;
}

export function sumBetween(
  txs: HomeAmount[],
  kind: 'income' | 'expense',
  from: number,
  to: number,
): number {
  return txs
    .filter((t) => t.kind === kind && inRange(t.at, from, to))
    .reduce((s, t) => s + t.amount, 0);
}

/** Average full-month figure from the transaction span; null with <1 data point. */
export function averageMonthly(txs: HomeAmount[], kind: 'income' | 'expense', now: number): number | null {
  const matching = txs.filter((t) => t.kind === kind);
  if (matching.length === 0) return null;
  const earliest = Math.min(...matching.map((t) => t.at));
  const spanMonths = Math.max(1, (now - earliest) / (30 * DAY));
  const total = matching.reduce((s, t) => s + t.amount, 0);
  return total / spanMonths;
}

export function computeBudgetSnapshot(
  txs: HomeAmount[],
  profile: WalletProfile,
  unconvertibleCount: number,
  now: number,
): BudgetSnapshot {
  const goal = profile.monthlySpendingGoal;
  const mStart = monthStart(now);
  const dim = daysInMonth(now);
  const dayOfMonth = new Date(now).getUTCDate();

  const spentThisMonth = sumBetween(txs, 'expense', mStart, now + 1);
  const incomeThisMonth = sumBetween(txs, 'income', mStart, now + 1);
  const hasMonthData = txs.some((t) => inRange(t.at, mStart, now + 1));

  const dailyBudget = goal != null ? goal / dim : null;
  const weeklyBudget = goal != null ? (goal / dim) * 7 : null;
  const remainingBudget = goal != null ? goal - spentThisMonth : null;
  const projectedSpending = hasMonthData && dayOfMonth > 0
    ? (spentThisMonth / dayOfMonth) * dim
    : null;
  const savingsRate = incomeThisMonth > 0
    ? (incomeThisMonth - spentThisMonth) / incomeThisMonth
    : null;

  // Runway: lifetime net savings divided by average monthly expenses.
  const avgExpenses = averageMonthly(txs, 'expense', now);
  const lifetimeNet =
    txs.filter((t) => t.kind === 'income').reduce((s, t) => s + t.amount, 0) -
    txs.filter((t) => t.kind === 'expense').reduce((s, t) => s + t.amount, 0);
  const runwayMonths = avgExpenses != null && avgExpenses > 0 && lifetimeNet > 0
    ? lifetimeNet / avgExpenses
    : null;

  let health: BudgetHealth = 'unknown';
  if (goal != null && hasMonthData) {
    if (spentThisMonth > goal) health = 'over-budget';
    else if (projectedSpending != null && projectedSpending > goal) health = 'warning';
    else health = 'healthy';
  }

  return {
    currency: profile.homeCurrency,
    dailyBudget,
    weeklyBudget,
    monthlyBudget: goal,
    remainingBudget,
    projectedSpending,
    savingsRate,
    runwayMonths,
    unconvertibleCount,
    health,
  };
}

/** Cost-of-living comparison across visited cities, from real spend only. */
export function cityCosts(
  txs: HomeAmount[],
  stays: { city: string; arrivedAt: number; departedAt: number | null }[],
  now: number,
): CityCost[] {
  const byCity = new Map<string, { spent: number; days: number }>();
  for (const stay of stays) {
    const days = Math.max(1, Math.floor(((stay.departedAt ?? now) - stay.arrivedAt) / DAY));
    const entry = byCity.get(stay.city) ?? { spent: 0, days: 0 };
    entry.days += days;
    byCity.set(stay.city, entry);
  }
  for (const t of txs) {
    if (t.kind !== 'expense' || !t.city) continue;
    const entry = byCity.get(t.city) ?? { spent: 0, days: 0 };
    entry.spent += t.amount;
    byCity.set(t.city, entry);
  }
  return Array.from(byCity.entries())
    .map(([city, { spent, days }]) => ({
      city,
      totalSpent: spent,
      days,
      perDay: days > 0 && spent > 0 ? spent / days : null,
    }))
    .sort((a, b) => (a.perDay ?? Infinity) - (b.perDay ?? Infinity));
}
